// frontend/src/components/common/SpecialPricingDisplay.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useLandingTranslation } from '../../hooks/useLandingTranslation';
import { getCountryText } from '../../utils/countryDetection';

// Add keyframes for pulse animation
const styles = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 20px rgba(13, 110, 253, 0.3);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 25px rgba(13, 110, 253, 0.5);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 20px rgba(13, 110, 253, 0.3);
    }
  }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.querySelector('#special-pricing-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'special-pricing-styles';
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

const SpecialPricingDisplay = ({ 
  pricingData, 
  userLocation, 
  loadingPricing = false, 
  getOriginalPrice, 
  getFormattedPrice,
  className = "",
  // Optional image props
  showImage = false,
  imageSrc = "/images/ctaimage/pzpointingleft.png",
  imageAlt = "Profe Zac",
  imagePosition = "right", // "left", "right", "top", "bottom"
  imageMaxWidth = "200px",
  // Pricing configuration
  showRealValue = true, // Show the "real value" price
  showCountryDiscount = true, // Show the "country discount" price
  realValuePrice = '$20.00',
  countryDiscountPrice = '$10.00'
}) => {
  const { currentLanguage } = useLanguage();
  const { t } = useLandingTranslation('profezac');
  
  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [currentDate, setCurrentDate] = useState('');

  // Countdown timer to midnight
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0); // Next midnight
      
      const difference = midnight.getTime() - now.getTime();
      
      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ hours, minutes, seconds });
      } else {
        // Reset for next day
        setTimeLeft({ hours: 24, minutes: 0, seconds: 0 });
      }

      // Update current date
      const today = now.toLocaleDateString(currentLanguage === 'es' ? 'es-ES' : 
                                           currentLanguage === 'pt' ? 'pt-BR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      setCurrentDate(today);
    };

    // Update immediately
    updateCountdown();
    
    // Update every second
    const timer = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(timer);
  }, [currentLanguage]);

  const getDateValidityText = () => {
    const texts = {
      'es': `Las ofertas se renuevan diariamente, esta oferta es válida solo para: ${currentDate}`,
      'en': `Offers are renewed daily, this offer is valid only for: ${currentDate}`,
      'pt': `As ofertas são renovadas diariamente, esta oferta é válida apenas para: ${currentDate}`
    };
    return texts[currentLanguage] || texts['es'];
  };

  // Image component for reuse
  const ImageComponent = () => (
    <div className="text-center mt-3 mt-md-0">
      <img 
        src={imageSrc} 
        alt={imageAlt} 
        className="img-fluid rounded" 
        style={{ maxWidth: imageMaxWidth }}
      />
    </div>
  );

  // Pricing content component
  const PricingContent = () => (
    <div>
      {/* Original Price - Crossed Out Card (conditional) */}
      {showRealValue && (
        <div className="mb-2" style={{
          background: 'rgba(108, 117, 125, 0.1)',
          border: '1px dashed rgba(108, 117, 125, 0.3)',
          borderRadius: '6px',
          padding: '10px',
          opacity: '0.7'
        }}>
          <div className="text-dark small fw-medium mb-1" style={{ fontSize: '0.7rem', opacity: 0.8 }}>
            {t('final_cta.pricing.real_value_label')}
          </div>
          <div className="fs-6 fw-bold text-danger text-decoration-line-through" style={{ opacity: 0.6 }}>
            {loadingPricing ? realValuePrice : getOriginalPrice('basic', 'monthly', 4, realValuePrice).replace(/\/.*/, '')}
          </div>
        </div>
      )}
      
      {/* Country Discount - Also Crossed Out Card (conditional) */}
      {showCountryDiscount && (
        <div className="mb-3" style={{
          background: 'rgba(108, 117, 125, 0.1)',
          border: '1px dashed rgba(108, 117, 125, 0.3)',
          borderRadius: '6px',
          padding: '10px',
          opacity: '0.7'
        }}>
          <div className="text-dark small fw-medium mb-1" style={{ fontSize: '0.7rem', opacity: 0.8 }}>
            {t('final_cta.pricing.discount_for_label')} {userLocation ? getCountryText(userLocation.countryCode, currentLanguage).name + ' ' + getCountryText(userLocation.countryCode, currentLanguage).emoji : 'latinos en 🇺🇸'}:
          </div>
          <div className="fs-6 fw-bold text-warning text-decoration-line-through" style={{ opacity: 0.6 }}>
            {loadingPricing ? countryDiscountPrice : getOriginalPrice('basic', 'monthly', 2, countryDiscountPrice).replace(/\/.*/, '')}
          </div>
        </div>
      )}
      
      {/* Final Price - Big and Prominent with Savings */}
      <div className="position-relative">
        <div 
          className="bg-primary bg-opacity-10 rounded-3 p-3 border border-primary border-opacity-25"
          style={{
            animation: 'pulse 2s infinite',
            boxShadow: '0 0 20px rgba(13, 110, 253, 0.3)'
          }}
        >
          <div className="text-center">
            <div className="text-primary fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
              {t('final_cta.pricing.your_price_today')}
            </div>
            <div className="display-4 fw-bold text-primary mb-1" style={{ lineHeight: '1' }}>
              {loadingPricing ? '$5.00' : getFormattedPrice('basic', 'monthly', '$5.00')}
            </div>
            <div className="d-flex justify-content-center align-items-center gap-3">
              <div className="text-success small fw-bold" style={{ fontSize: '0.7rem' }}>
                🎉 {t('final_cta.pricing.you_save')} {loadingPricing ? '75%' : Math.round((1 - (5 / 20)) * 100) + '%'}!
              </div>
              <div className="text-primary small" style={{ fontSize: '0.7rem' }}>
                🔥 {t('final_cta.pricing.lowest_price_ever')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Countdown Below Price */}
      <div className="bg-danger bg-opacity-90 rounded p-2 mt-3 border border-danger">
        <div className="text-white fw-bold text-center mb-2" style={{ fontSize: '1.1rem' }}>
          ⏰ {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
        </div>
        <div className="text-white small text-center" style={{ fontSize: '0.7rem', opacity: 0.95 }}>
          {getDateValidityText()}
        </div>
      </div>
    </div>
  );

  // Render based on image configuration
  if (!showImage) {
    return (
      <div className={className}>
        <PricingContent />
      </div>
    );
  }

  // With image layouts
  if (imagePosition === 'top') {
    return (
      <div className={className}>
        <div className="mb-4">
          <ImageComponent />
        </div>
        <PricingContent />
      </div>
    );
  }

  if (imagePosition === 'bottom') {
    return (
      <div className={className}>
        <PricingContent />
        <div className="mt-4">
          <ImageComponent />
        </div>
      </div>
    );
  }

  // Side layouts (left/right)
  return (
    <div className={`row align-items-center ${className}`}>
      {imagePosition === 'left' && (
        <div className="col-md-4">
          <ImageComponent />
        </div>
      )}
      <div className={`${imagePosition === 'left' || imagePosition === 'right' ? 'col-md-8' : 'col-12'} text-md-start text-center`}>
        <PricingContent />
      </div>
      {imagePosition === 'right' && (
        <div className="col-md-4">
          <ImageComponent />
        </div>
      )}
    </div>
  );
};

export default SpecialPricingDisplay;